import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '~/tools/makeStyles';
import combineStyles from 'classnames';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import IconButton from '@mui/material/IconButton';
import Loading from './Loading';
import LanguageSelect from './LanguageSelect';
import PreviewEmployeeCombobox from './PreviewEmployeeCombobox';
import { resetApp, fetchSurvey } from '../redux/actions';
import { createKioskUriWithQueryParams } from '../helpers/link_helper';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../redux/ReduxProvider';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    height: theme.measurements.headerHeight,
    boxShadow: '0px 3px 15px rgba(100, 100, 100, 0.49)',
  },
  toolbar: {
    margin: 0,
    padding: 8,
    display: 'flex',
    justifyContent: 'space-between',
    listStyle: 'none',
    '& > li': {
      position: 'relative',
      [theme.breakpoints.down(400)]: {
        minWidth: 'auto',
      },
      '&.middle': {
        width: 'auto',
        overflow: 'hidden',
      },
    },
  },
  languageSelect: {
    marginTop: 8,
    marginLeft: 20,
    color: '#000',
    opacity: '.5',
    [theme.breakpoints.down(400)]: {
      marginLeft: 0,
      maxWidth: 100,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&:hover': {
      opacity: '.8',
    },
    '& .MuiInputBase-input': {
      color: '#000',
    },
    '& .MuiSelect-icon': {
      color: '#000',
      fill: '#000',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
    '&.darkmode': {
      color: '#fff',
      '& .MuiInputBase-input': {
        color: '#fff',
      },
      '& .MuiSelect-icon': {
        color: '#fff',
        fill: '#fff',
      },
    },
  },
  exitButton: {
    color: '#000',
    opacity: '.5',
    '&.darkmode': {
      color: '#fff',
    },
    '&:hover': {
      opacity: '.8',
    },
  },
  logo: {
    height: '3rem',
    [theme.breakpoints.down(400)]: {
      height: '2rem',
      transform: 'translateY(25%)',
      overflow: 'hidden',
    },
  },
  loadingBar: {
    position: 'absolute',
    top: theme.measurements.headerHeight,
    left: 0,
    right: 0,
  },
  skipLink: {
    position: 'absolute',
    top: 8,
    left: 8,
    textDecoration: 'none',
    color: 'transparent',
    width: 0,
    height: 0,
  },
  visibleSkipLink: {
    width: 'unset',
    height: 'unset',
    color: '#000000',
    marginBottom: 10,
  },
}));

// returns "black" if not 6 char string
const hexToRgb = (hex) => {
  if (hex.length !== 6) return [0, 0, 0];

  const rgbHex = hex.match(/.{1,2}/g);
  const rgb = [parseInt(rgbHex[0], 16), parseInt(rgbHex[1], 16), parseInt(rgbHex[2], 16)];

  return rgb;
};

// @see https://en.wikipedia.org/wiki/Relative_luminance
const isDarkColor = (hexColor = '#000000') => {
  const [r, g, b] = hexToRgb(hexColor.replace('#', ''));
  const cofR = 0.2126;
  const cofG = 0.7152;
  const cofB = 0.0722;
  const luminanceThreshold = 125;
  const luminance = cofR * r + cofG * g + cofB * b;
  const isDark = luminance < luminanceThreshold;

  return isDark;
};

const SurveyHeader = ({
  color,
  backgroundColor,
  logoUrl = '',
  isLoading = false,
  isKiosk = false,
  previewTokenReady = false,
}) => {
  // Background color can be null, so we have to handle that specially
  const colorOrDefault = color || '#000000';
  const backgroundColorOrDefault = backgroundColor || '#ffffff';
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isSkipLinkVisible, setIsSkipLinkVisible] = useState(false);

  const createKioskUri = createKioskUriWithQueryParams(location.search);
  const { kioskId, name: companyName } = useAppSelector((state) => state.company);
  const { isPreview, surveyCode } = useAppSelector((state) => state.survey);
  const isDark = isDarkColor(backgroundColorOrDefault);

  const handleSelectPreviewEmployee = (employee) => {
    dispatch(fetchSurvey(surveyCode, employee ? employee.id : null));
  };

  const logout = () => {
    dispatch(resetApp());
    navigate(createKioskUri(kioskId));
  };

  const activeSkipLink = () => {
    setIsSkipLinkVisible(true);
  };

  const deactiveSkipLink = () => {
    setIsSkipLinkVisible(false);
  };

  return (
    <header
      className={styles.root}
      style={{
        backgroundColor: backgroundColorOrDefault,
        color: colorOrDefault,
      }}
      role="banner"
    >
      <a
        className={combineStyles(styles.skipLink, {
          [styles.visibleSkipLink]: isSkipLinkVisible,
        })}
        href="#main"
        onFocus={activeSkipLink}
        onBlur={deactiveSkipLink}
      >
        {t('accessibility.skipToContent')}
      </a>
      <ul className={styles.toolbar} role="presentation">
        <li>
          <LanguageSelect className={combineStyles(styles.languageSelect, { darkmode: isDark })} />
        </li>
        <li className="middle">
          {logoUrl && <img className={styles.logo} src={logoUrl} alt={companyName || 'company-logo'} aria-hidden />}
        </li>
        <li>
          {isPreview && previewTokenReady && (
            <PreviewEmployeeCombobox surveyCode={surveyCode} onChange={handleSelectPreviewEmployee} />
          )}
          {!isPreview && isKiosk && (
            <IconButton
              className={combineStyles(styles.exitButton, { darkmode: isDark })}
              aria-label="exit"
              onClick={logout}
              size="large"
            >
              <ExitToAppIcon />
            </IconButton>
          )}
        </li>
      </ul>
      <Loading isVisible={isLoading} className={styles.loadingBar} />
    </header>
  );
};

SurveyHeader.propTypes = {
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  logoUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  isKiosk: PropTypes.bool,
  previewTokenReady: PropTypes.bool,
};

export default SurveyHeader;
